import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CopilotSocketEvents,
  CopilotSuggestion,
  CopilotSuggestionType,
  CopilotSuggestionsPayload,
  MissingTravelField,
  TravelIntelligenceSuggestion,
} from '@bandido/shared/types';
import { useWS } from '@frontend/features/common/hooks/useWS';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { CONFIG } from '@frontend/config';

const copilotCaseRoom = (caseId: number) => `copilot_case_${caseId}`;

/**
 * Se conecta al namespace /copilot, se une al room del caso y mantiene el
 * último set de sugerencias recibidas por Socket.IO.
 */
export const useCopilotSuggestions = (caseId?: number) => {
  const { isConnected, socketRef, joinRoom, leaveRoom } = useWS(
    `${CONFIG.WS_URL}/copilot`,
  );
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<CopilotSuggestion[]>([]);
  // Ref al último set de sugerencias, para leer el target del dismiss sin closure
  // viejo (y poder restaurar/refetchear ante fallo).
  const suggestionsRef = useRef<CopilotSuggestion[]>([]);
  useEffect(() => {
    suggestionsRef.current = suggestions;
  }, [suggestions]);
  // El server autoriza el acceso al caso antes de sumarnos al room. Solo cuando
  // el join está confirmado disparamos el análisis: así evitamos la carrera en
  // la que el resultado se emite antes de que estemos efectivamente en el room.
  const [joined, setJoined] = useState(false);

  // Reset al cambiar de caso.
  useEffect(() => {
    setSuggestions([]);
    setJoined(false);
  }, [caseId]);

  // Unirse / salir del room del caso.
  useEffect(() => {
    if (!isConnected || !caseId) return;
    const room = copilotCaseRoom(caseId);
    joinRoom(room);
    return () => {
      setJoined(false);
      leaveRoom(room);
    };
  }, [isConnected, caseId, joinRoom, leaveRoom]);

  // Confirmación de join emitida por el gateway ('joinedRoom'). Si el acceso se
  // deniega ('joinDenied'), nunca marcamos joined y no se dispara el análisis.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !caseId) return;
    const room = copilotCaseRoom(caseId);
    const onJoined = (joinedRoom: unknown) => {
      if (joinedRoom === room) setJoined(true);
    };
    socket.on('joinedRoom', onJoined);
    return () => {
      socket.off('joinedRoom', onJoined);
    };
  }, [socketRef, caseId, isConnected]);

  // Una vez confirmado el join, cargar las últimas sugerencias cacheadas. Si no
  // hay, disparar análisis pasivo: el resultado llega por WebSocket al listener.
  useEffect(() => {
    if (!joined || !caseId) return;
    let cancelled = false;
    client
      .get<CopilotSuggestion[]>(`copilot/${caseId}/suggestions`)
      .then((response) => {
        if (cancelled) return;
        const cached = response.data;
        if (Array.isArray(cached) && cached.length > 0) {
          setSuggestions(cached);
        } else {
          // Cache miss: el caso no tiene sugerencias. Disparar análisis pasivo.
          // El resultado llega por WebSocket al listener de arriba.
          void client.post(`copilot/cases/${caseId}/analyze`).catch(() => {
            // Non-critical: si falla, esperamos el próximo mensaje entrante.
          });
        }
      })
      .catch(() => {
        // Si falla el GET, esperamos el próximo evento de WebSocket.
      });
    return () => {
      cancelled = true;
    };
  }, [joined, caseId]);

  // Escuchar sugerencias.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !caseId) return;

    const handler = (raw: unknown) => {
      try {
        const payload: CopilotSuggestionsPayload =
          typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (payload?.caseId === caseId) {
          setSuggestions(payload.suggestions ?? []);
        }
      } catch {
        // fallback gracioso: ignoramos payloads malformados
      }
    };

    socket.on(CopilotSocketEvents.SUGGESTIONS, handler);
    return () => {
      socket.off(CopilotSocketEvents.SUGGESTIONS, handler);
    };
  }, [socketRef, caseId, isConnected]);

  // Pide sugerencias de respuesta on-demand (modelo activo). Usa la respuesta
  // HTTP como fuente de verdad además del WebSocket: si el socket se pierde o
  // hay carrera con el join, igual mostramos el resultado que ya se pagó.
  const requestReply = useCallback(async () => {
    if (!caseId) return;
    const response = await client.post<CopilotSuggestion[]>(
      `copilot/cases/${caseId}/suggest-reply`,
    );
    const data = response.data;
    if (Array.isArray(data) && data.length > 0) {
      setSuggestions(data);
    }
  }, [caseId]);

  // On-demand: el agente toca un botón de facet. Trae la card (y la mergea,
  // reemplazando una card previa del mismo tipo) o devuelve qué falta para el
  // popover.
  const requestFacet = useCallback(
    async (
      facet: string,
      overrides?: Record<string, string>,
    ): Promise<{
      missingFields: MissingTravelField[];
      found: boolean;
      throttled: boolean;
    }> => {
      if (!caseId) return { missingFields: [], found: false, throttled: false };
      const response = await client.post<{
        cards: CopilotSuggestion[];
        missingFields: MissingTravelField[];
        throttled?: boolean;
      }>(`copilot/cases/${caseId}/travel-intelligence`, { facet, overrides });
      const { cards, missingFields, throttled } = response.data;
      const found = Array.isArray(cards) && cards.length > 0;
      if (found) {
        setSuggestions((prev) => {
          const newTypes = new Set(
            cards
              .filter(
                (c) => c.type === CopilotSuggestionType.TRAVEL_INTELLIGENCE,
              )
              .map((c) => (c as TravelIntelligenceSuggestion).cardType),
          );
          const kept = prev.filter(
            (s) =>
              s.type !== CopilotSuggestionType.TRAVEL_INTELLIGENCE ||
              !newTypes.has(s.cardType),
          );
          return [...cards, ...kept];
        });
      }
      return {
        missingFields: missingFields ?? [],
        found,
        throttled: Boolean(throttled),
      };
    },
    [caseId],
  );

  // Descarta una sugerencia del panel. Las persistentes (captura de datos) se
  // tumban server-side (contrato durable): si el server RECHAZA el descarte, NO lo
  // tragamos — refetcheamos la lista autoritativa y avisamos, así la UI no queda
  // diciendo que se descartó cuando en realidad no se persistió.
  const dismiss = useCallback(
    (index: number) => {
      const target = suggestionsRef.current[index];
      const isPersistent =
        target &&
        (target.type === CopilotSuggestionType.UPDATE_CUSTOM_FIELD ||
          target.type === CopilotSuggestionType.UPDATE_CLIENT_NAME);

      // Remoción optimista.
      setSuggestions((prev) => prev.filter((_, i) => i !== index));

      if (!caseId || !isPersistent) return;

      const body =
        target.type === CopilotSuggestionType.UPDATE_CUSTOM_FIELD
          ? { type: target.type, fieldName: target.fieldName, value: target.value }
          : { type: target.type, value: target.suggestedName };

      void client
        .post(`copilot/cases/${caseId}/suggestions/dismiss`, body)
        .catch(async () => {
          // El server no persistió el descarte: recuperamos el estado autoritativo
          // (la sugerencia vuelve) y avisamos del error.
          try {
            const res = await client.get<CopilotSuggestion[]>(
              `copilot/${caseId}/suggestions`,
            );
            if (Array.isArray(res.data)) setSuggestions(res.data);
          } catch {
            // si el refetch también falla, dejamos el panel como está
          }
          addToast({ message: t('copilot.panel.dismissError'), color: 'error' });
        });
    },
    [caseId, addToast, t],
  );

  // Cuando el agente envía un mensaje, cualquier respuesta sugerida queda
  // obsoleta (ya respondió). La quitamos del panel al instante.
  const clearReplies = useCallback(() => {
    setSuggestions((prev) =>
      prev.filter((s) => s.type !== CopilotSuggestionType.SUGGEST_REPLY),
    );
  }, []);

  return {
    suggestions,
    dismiss,
    clearReplies,
    requestReply,
    requestFacet,
    isConnected,
  };
};
